/* ==========================================================================
   Custom Search Drawer
   - Opens full-screen from any [data-csd-open] trigger.
   - AJAX predictive search rendered by the 'predictive-search-cards' section.
   - Browse state with collection tabs + product grid when query is empty.
   ========================================================================== */
(function () {
  if (window.customElements.get('custom-search-drawer')) return;

  class CustomSearchDrawer extends HTMLElement {
    constructor() {
      super();
      this.panel = this.querySelector('.csd__panel');
      this.input = this.querySelector('[data-csd-input]');
      this.clearBtn = this.querySelector('[data-csd-clear]');
      this.browse = this.querySelector('[data-csd-browse]');
      this.results = this.querySelector('[data-csd-results]');
      this.resultsTarget = this.querySelector('[data-csd-results-target]');
      this.loading = this.querySelector('[data-csd-loading]');
      this.form = this.querySelector('[data-csd-form]');
      this.recent = this.querySelector('[data-csd-recent]');
      this.recentList = this.querySelector('[data-csd-recent-list]');
      this.recentClear = this.querySelector('[data-csd-recent-clear]');

      this.searchUrl = this.dataset.searchUrl || (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'search' : '/search');
      this.minChars = parseInt(this.dataset.minChars, 10) || 2;
      this.recentKey = 'csd:recent';
      this.recentMax = 8;

      this.controller = null;
      this.debounce = null;
      this.cache = {};
      this.isOpen = false;

      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      // Open triggers (delegated so it covers every search icon in the header)
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-csd-open]');
        if (opener) {
          e.preventDefault();
          this.open();
        }
      });

      // Close triggers
      this.querySelectorAll('[data-csd-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      // Input
      if (this.input) {
        this.input.addEventListener('input', () => this.onInput());
      }
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => this.clear());
      }
      // Submit -> remember term, then go to full search results page
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          const q = this.input ? this.input.value.trim() : '';
          if (!q) { e.preventDefault(); return; }
          this.addRecent(q);
        });
      }

      // Remember the term when a result link is clicked (navigation)
      if (this.results) {
        this.results.addEventListener('click', (e) => {
          if (e.target.closest('a')) {
            const q = this.input ? this.input.value.trim() : '';
            if (q) this.addRecent(q);
          }
        });
      }

      // Recent searches: clear all
      if (this.recentClear) {
        this.recentClear.addEventListener('click', () => this.clearRecent());
      }

      this.initTabs();
    }

    /* ---------------- open / close ---------------- */
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('csd-open');
      document.body.classList.add('csd-open');
      document.addEventListener('keydown', this.onKeydown);
      this.renderRecent();
      // Focus after the slide-in starts
      setTimeout(() => { if (this.input) this.input.focus(); }, 120);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('csd-open');
      document.body.classList.remove('csd-open');
      document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    }

    /* ---------------- input handling ---------------- */
    onInput() {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;

      clearTimeout(this.debounce);

      if (q.length < this.minChars) {
        this.showBrowse();
        return;
      }
      this.debounce = setTimeout(() => this.search(q), 180);
    }

    /* ---------------- recent searches (localStorage) ---------------- */
    getRecent() {
      try {
        const raw = window.localStorage.getItem(this.recentKey);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    }

    addRecent(term) {
      term = (term || '').trim();
      if (!term) return;
      try {
        let list = this.getRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
        list.unshift(term);
        list = list.slice(0, this.recentMax);
        window.localStorage.setItem(this.recentKey, JSON.stringify(list));
      } catch (e) { /* storage unavailable */ }
    }

    clearRecent() {
      try { window.localStorage.removeItem(this.recentKey); } catch (e) { /* noop */ }
      this.renderRecent();
    }

    renderRecent() {
      if (!this.recent || !this.recentList) return;
      const list = this.getRecent();
      if (!list.length) {
        this.recent.hidden = true;
        this.recentList.innerHTML = '';
        return;
      }
      const ico = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
      this.recentList.innerHTML = list
        .map((t) => `<button type="button" class="csd__chip" data-csd-chip>${ico}<span>${this.escapeHtml(t)}</span></button>`)
        .join('');
      this.recent.hidden = false;

      this.recentList.querySelectorAll('[data-csd-chip]').forEach((chip) => {
        chip.addEventListener('click', () => {
          const term = chip.textContent.trim();
          this.input.value = term;
          if (this.clearBtn) this.clearBtn.hidden = false;
          this.addRecent(term);
          this.search(term);
          this.input.focus();
        });
      });
    }

    escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }

    clear() {
      this.input.value = '';
      if (this.clearBtn) this.clearBtn.hidden = true;
      this.showBrowse();
      this.input.focus();
    }

    showBrowse() {
      if (this.browse) this.browse.hidden = false;
      if (this.results) this.results.hidden = true;
      this.renderRecent();
    }

    showResults() {
      if (this.browse) this.browse.hidden = true;
      if (this.results) this.results.hidden = false;
    }

    /* ---------------- AJAX search ---------------- */
    search(q) {
      this.showResults();

      if (this.cache[q]) {
        this.render(this.cache[q], q);
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      if (this.loading) this.loading.hidden = false;
      this.results.setAttribute('aria-busy', 'true');

      const params = new URLSearchParams({
        q: q,
        type: 'product,article,page',
        'options[prefix]': 'last',
        section_id: 'predictive-search-cards',
      });

      fetch(`${this.searchUrl}?${params.toString()}`, { signal: this.controller.signal })
        .then((r) => {
          if (!r.ok) throw new Error('search failed');
          return r.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('[data-csd-results-inner]');
          const out = inner ? inner.outerHTML : '';
          this.cache[q] = out;
          // Only render if this is still the current query
          if (this.input.value.trim() === q) this.render(out, q);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          if (this.input.value.trim() === q) this.render('', q);
        });
    }

    render(html, q) {
      if (this.loading) this.loading.hidden = true;
      this.results.removeAttribute('aria-busy');

      if (!html) {
        // Empty/failed response: never leave the panel blank.
        const term = this.escapeHtml(q || (this.input ? this.input.value.trim() : ''));
        const url = `${this.searchUrl}?q=${encodeURIComponent(q || '')}`;
        this.resultsTarget.innerHTML =
          '<div class="csd-results-inner"><div class="csd-empty">' +
          `<p class="csd-empty__title">לא נמצאו תוצאות עבור &ldquo;${term}&rdquo;</p>` +
          `<a class="csd-empty__link" href="${url}">חיפוש עבור &ldquo;${term}&rdquo;</a>` +
          '</div></div>';
        return;
      }

      this.resultsTarget.innerHTML = html;
      // Re-init homepage product-card galleries / lazy images for fresh cards.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    /* ---------------- browse tabs ---------------- */
    initTabs() {
      const wrap = this.querySelector('[data-csd-tabs]');
      if (!wrap) return;
      const buttons = wrap.querySelectorAll('.csd__tab');
      const panels = this.querySelectorAll('.csd__panel-tab');

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const targetId = btn.getAttribute('data-csd-tab-target');
          buttons.forEach((b) => {
            const active = b === btn;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          panels.forEach((p) => {
            p.classList.toggle('is-active', p.id === targetId);
          });
          // Newly visible cards need their galleries initialised.
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        });
      });
    }
  }

  window.customElements.define('custom-search-drawer', CustomSearchDrawer);
})();
